import React from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';

/**
 * Classic Elegant Theme
 * Timeless design with serif fonts, traditional business look
 */
const ClassicElegantTheme = ({ document, settings = {} }) => {
  const primaryColor = settings.theme?.primary_color || '#2C3E50';
  const accentColor = settings.theme?.secondary_color || '#8B4513';
  const fontFamily = settings.typography?.font_family || 'Palatino, Georgia, serif';
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    if (num === 0) return 'Zero Rupees Only';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor((num % 1000) / 100);
    const remainder = Math.floor(num % 100);
    let words = '';
    if (crore > 0) words += ones[crore] + ' Crore ';
    if (lakh > 0) words += (lakh < 20 && lakh > 9 ? teens[lakh - 10] : (tens[Math.floor(lakh / 10)] + ' ' + ones[lakh % 10])) + ' Lakh ';
    if (thousand > 0) words += (thousand < 20 && thousand > 9 ? teens[thousand - 10] : (tens[Math.floor(thousand / 10)] + ' ' + ones[thousand % 10])) + ' Thousand ';
    if (hundred > 0) words += ones[hundred] + ' Hundred ';
    if (remainder > 0) {
      if (remainder < 10) words += ones[remainder];
      else if (remainder < 20) words += teens[remainder - 10];
      else words += tens[Math.floor(remainder / 10)] + ' ' + ones[remainder % 10];
    }
    return words.trim() + ' Rupees Only';
  };

  return (
    <div className="print-template classic-elegant" style={{ fontFamily, padding: '45px', maxWidth: '210mm', margin: '0 auto', background: '#fff' }}>
      {/* Decorative Header Border */}
      <div style={{ borderTop: '4px double #8B4513', borderBottom: '1px solid #8B4513', padding: '25px 0', marginBottom: '35px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {settings.branding?.logo_url && (
              <img src={settings.branding.logo_url} alt="Logo" style={{ maxHeight: '75px', marginBottom: '15px' }} />
            )}
            <h1 style={{ fontSize: '32px', fontWeight: '400', color: primaryColor, margin: '0', fontStyle: 'italic', letterSpacing: '0.5px' }}>
              {settings.branding?.company_name || 'COMPANY NAME'}
            </h1>
            <p style={{ fontSize: '11px', color: '#555', lineHeight: '1.7', maxWidth: '350px', marginTop: '10px', fontStyle: 'italic' }}>
              {settings.branding?.address || '123 Business Street, City, State - 123456'}
            </p>
          </div>
          <div style={{ textAlign: 'center', borderLeft: '1px solid #8B4513', paddingLeft: '30px' }}>
            <div style={{ fontSize: '14px', fontWeight: '400', color: '#8B4513', letterSpacing: '3px', marginBottom: '10px', fontStyle: 'italic' }}>QUOTATION</div>
            <div style={{ fontSize: '24px', fontWeight: '600', color: primaryColor, marginBottom: '12px' }}>{document.quotation_no || 'QT-001'}</div>
            <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.8' }}>
              <div><em>Date:</em> {new Date(document.doc_date || Date.now()).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bill To - Classic Style */}
      <div style={{ border: '1px solid #DDD', borderRadius: '6px', padding: '25px', marginBottom: '35px', background: '#FAFAFA' }}>
        <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '2px', color: '#8B4513', marginBottom: '15px', textTransform: 'uppercase' }}>To</div>
        <div style={{ fontSize: '18px', fontWeight: '600', color: primaryColor, marginBottom: '10px', fontStyle: 'italic' }}>{document.party?.name || 'Customer Name'}</div>
        {document.party?.phone && <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Phone: {document.party.phone}</div>}
        {document.party?.billing_address && (
          <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.8', marginTop: '12px' }}>
            {document.party.billing_address.line1}<br />
            {document.party.billing_address.city}, {document.party.billing_address.state} - {document.party.billing_address.pincode}
          </div>
        )}
        {settings.toggles?.show_gstin && document.party?.gstin && (
          <div style={{ fontSize: '11px', color: '#666', marginTop: '10px', paddingTop: '10px', borderTop: '1px dotted #CCC' }}>
            <em>GSTIN:</em> {document.party.gstin}
          </div>
        )}
      </div>

      {/* Items Table - Traditional */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '35px', border: '1px solid #DDD' }}>
        <thead>
          <tr style={{ background: '#F5F5F5', borderBottom: '2px solid #8B4513' }}>
            <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#555', width: '5%' }}>#</th>
            <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#555' }}>Particulars</th>
            {settings.toggles?.show_hsn && <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#555', width: '12%' }}>HSN/SAC</th>}
            <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#555', width: '10%' }}>Quantity</th>
            <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#555', width: '12%' }}>Rate</th>
            <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#555', width: '15%' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {(document.items || []).map((item, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #E8E8E8' }}>
              <td style={{ padding: '14px 10px', fontSize: '11px', color: '#888' }}>{index + 1}.</td>
              <td style={{ padding: '14px 10px', fontSize: '13px', color: '#333' }}>
                {item.name}
                {item.description && <div style={{ fontSize: '10px', color: '#888', marginTop: '4px', fontStyle: 'italic' }}>{item.description}</div>}
              </td>
              {settings.toggles?.show_hsn && <td style={{ padding: '14px 10px', textAlign: 'center', fontSize: '11px', color: '#666' }}>{item.hsn || '—'}</td>}
              <td style={{ padding: '14px 10px', textAlign: 'right', fontSize: '12px', color: '#666' }}>{item.quantity} {item.unit || 'PCS'}</td>
              <td style={{ padding: '14px 10px', textAlign: 'right', fontSize: '12px', color: '#666' }}>{formatCurrency(item.unit_price)}</td>
              <td style={{ padding: '14px 10px', textAlign: 'right', fontSize: '13px', color: '#333', fontWeight: '600' }}>{formatCurrency(item.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals - Traditional Style */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
        <div style={{ width: '450px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #E8E8E8' }}>
                <td style={{ padding: '10px', fontSize: '13px', color: '#666' }}>Subtotal</td>
                <td style={{ padding: '10px', textAlign: 'right', fontSize: '13px', color: '#333', fontWeight: '600' }}>{formatCurrency(document.totals?.subtotal || 0)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #E8E8E8' }}>
                <td style={{ padding: '10px', fontSize: '13px', color: '#666' }}>Discount</td>
                <td style={{ padding: '10px', textAlign: 'right', fontSize: '13px', color: '#DC2626', fontWeight: '600' }}>- {formatCurrency(document.totals?.discount_total || 0)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #E8E8E8', background: '#FAFAFA' }}>
                <td style={{ padding: '10px', fontSize: '13px', color: '#666', fontWeight: '600' }}>Taxable Amount</td>
                <td style={{ padding: '10px', textAlign: 'right', fontSize: '13px', color: '#333', fontWeight: '600' }}>{formatCurrency(document.totals?.taxable_amount || 0)}</td>
              </tr>
              {settings.toggles?.show_tax_breakup && document.tax_breakup?.map((tax, idx) => (
                <React.Fragment key={idx}>
                  <tr style={{ borderBottom: '1px dotted #DDD' }}>
                    <td style={{ padding: '8px 10px', fontSize: '12px', color: '#666', paddingLeft: '20px' }}>CGST @ {tax.rate / 2}%</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '12px', color: '#333', fontWeight: '500' }}>{formatCurrency(tax.amount / 2)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px dotted #DDD' }}>
                    <td style={{ padding: '8px 10px', fontSize: '12px', color: '#666', paddingLeft: '20px' }}>SGST @ {tax.rate / 2}%</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '12px', color: '#333', fontWeight: '500' }}>{formatCurrency(tax.amount / 2)}</td>
                  </tr>
                </React.Fragment>
              ))}
              {document.totals?.round_off !== 0 && (
                <tr style={{ borderBottom: '1px solid #E8E8E8' }}>
                  <td style={{ padding: '8px 10px', fontSize: '12px', color: '#666' }}>Round Off</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '12px', color: '#333', fontWeight: '500' }}>{document.totals.round_off > 0 ? '+' : ''} {formatCurrency(Math.abs(document.totals.round_off))}</td>
                </tr>
              )}
              <tr style={{ background: '#F5F5F5', borderTop: '2px solid #8B4513', borderBottom: '2px solid #8B4513' }}>
                <td style={{ padding: '15px 10px', fontSize: '15px', fontWeight: '600', color: primaryColor, fontStyle: 'italic' }}>Total Amount</td>
                <td style={{ padding: '15px 10px', textAlign: 'right', fontSize: '18px', fontWeight: '700', color: primaryColor }}>{formatCurrency(document.totals?.grand_total || 0)}</td>
              </tr>
              {document.payment?.received_amount && (
                <>
                  <tr style={{ background: '#FFFBF0' }}>
                    <td style={{ padding: '10px', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>Amount Received</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontSize: '13px', color: '#333', fontWeight: '600' }}>{formatCurrency(document.payment.received_amount)}</td>
                  </tr>
                  <tr style={{ background: '#FFF9E6', borderTop: '1px solid #8B4513' }}>
                    <td style={{ padding: '12px 10px', fontSize: '14px', color: accentColor, fontWeight: '600', fontStyle: 'italic' }}>Balance Due</td>
                    <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '16px', fontWeight: '700', color: accentColor }}>{formatCurrency(document.totals.grand_total - document.payment.received_amount)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Amount in Words */}
      <div style={{ background: '#FAFAFA', border: '1px solid #8B4513', borderRadius: '6px', padding: '15px', marginBottom: '35px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', color: accentColor, marginBottom: '8px', letterSpacing: '1px', textTransform: 'uppercase' }}>Amount in Words</div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: primaryColor, textTransform: 'uppercase', fontStyle: 'italic' }}>
          {numberToWords(document.totals?.grand_total || 0)}
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #8B4513', paddingTop: '30px', marginTop: '30px' }}>
        <div style={{ display: 'flex', gap: '30px' }}>
          {document.terms?.notes_public && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#8B4513', marginBottom: '10px', letterSpacing: '1px', fontStyle: 'italic' }}>Terms & Conditions</div>
              <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.8' }}>
                {document.terms.notes_public}
              </div>
            </div>
          )}
          {settings.toggles?.show_bank_details && settings.bank_details && (
            <div style={{ width: '300px', background: '#FAFAFA', border: '1px solid #8B4513', borderRadius: '6px', padding: '15px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: accentColor, marginBottom: '10px', letterSpacing: '1px', fontStyle: 'italic' }}>Banking Details</div>
              <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.8' }}>
                <div><strong>Bank:</strong> {settings.bank_details.bank_name}</div>
                <div><strong>Account:</strong> {settings.bank_details.account_number}</div>
                <div><strong>IFSC:</strong> {settings.bank_details.ifsc}</div>
              </div>
            </div>
          )}
        </div>
        {settings.toggles?.show_signature && (
          <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              {settings.qr_code_data && <QRCode value={settings.qr_code_data} size={70} />}
            </div>
            <div style={{ textAlign: 'center', minWidth: '250px' }}>
              <div style={{ borderTop: '2px solid #8B4513', paddingTop: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#8B4513', fontStyle: 'italic' }}>For {settings.branding?.company_name || 'Company'}</div>
                <div style={{ fontSize: '10px', color: '#888', marginTop: '5px' }}>Authorized Signatory</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassicElegantTheme;